import { useCallback, useEffect, useRef, useState } from 'react';

import { listBedStates } from '../../services/api/admissionApi';
import { mapDoctorsResponse } from '../../services/api/visitApi';
import { fetchStaffList } from '../../services/api/staffApi';
import useDepartmentStore from '../../stores/departmentStore';
import { useScope } from '../../context/PermissionContext';
import { STAFF_ROLES } from '../../constants/staff';
import { BED_STATES } from '../../constants/admission';

/**
 * useDoctorRoster — SINGLE source for the admission doctor picker. Hydrates the
 * doctor→dept edges (store is single-flight + skip-once-done) and fetches the
 * staff roster ONCE, resolving display names the SAME way RegisterVisitForm does
 * (mapDoctorsResponse) so NO surface ever shows a raw member UUID. Consumed by
 * both AdmitPatientForm and ChangeDoctorDialog.
 *
 * Runtime-safety: the staff fetch fires once per mount (alive guard, hydrate dep
 * is a stable store action) — it cannot grow with renders.
 */
export function useDoctorRoster() {
  const hydrateAllMemberDepts = useDepartmentStore((s) => s.hydrateAllMemberDepts);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    hydrateAllMemberDepts();
    let alive = true;
    fetchStaffList().then((res) => {
      if (!alive || !res.success) return;
      const docs = (res.data || []).filter((m) => m.role === STAFF_ROLES.DOCTOR);
      setDoctors(mapDoctorsResponse(docs));
    });
    return () => { alive = false; };
  }, [hydrateAllMemberDepts]);

  return doctors;
}

/**
 * useBedStates — fetch the sparse BEDSTATE list ONCE and expose a
 * `bed_id → status` lookup PLUS the occupant (round-2 B1 — the occupant
 * identity was on the wire the whole way except this hop threw it away by
 * collapsing each row to its bare status string). Absent bed_id ≡ VACANT
 * (§2.7 / §3), so the lookup returns VACANT for any bed not in the map.
 *
 * `occupantOf(bedId) → { patient_name, ip_number } | null` — null for a
 * vacant/absent bed or a non-occupied one (cleaning/blocked/reserved carry
 * no occupant either). Consumed by WardBedPicker to render "Occupied by
 * <name> · <IP>" instead of a bare status label.
 *
 * Runtime-safety (memory rule: no request storms): the fetch fires exactly ONCE
 * per mount via a ref guard — no object/array in a dep array, no re-fire on
 * re-render. `reload()` is a STABLE useCallback for an explicit manual refresh
 * (e.g. after an attach-bed 409) — it does not grow with renders.
 */
export function useBedStates() {
  const [stateById, setStateById] = useState({}); // bed_id -> full BEDSTATE row
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const reload = useCallback(async () => {
    if (inFlight.current) return; // single-flight: dedup concurrent callers
    inFlight.current = true;
    setLoading(true);
    const res = await listBedStates();
    if (res.success) {
      const map = {};
      for (const s of res.data.states) {
        if (s && s.bed_id) map[s.bed_id] = { ...s, status: s.status || BED_STATES.OCCUPIED };
      }
      setStateById(map);
      setError(null);
    } else {
      setError(res.error || 'Failed to load bed availability.');
    }
    setLoading(false);
    inFlight.current = false;
  }, []);

  useEffect(() => {
    reload();
    // reload is stable (empty-dep useCallback) → this runs exactly once.
  }, [reload]);

  /** Absent ≡ VACANT. */
  const statusOf = useCallback(
    (bedId) => (stateById[bedId] && stateById[bedId].status) || BED_STATES.VACANT,
    [stateById],
  );

  /** Occupant identity for a non-vacant bed, or null (vacant/absent/no occupant on the row). */
  const occupantOf = useCallback(
    (bedId) => {
      const row = stateById[bedId];
      if (!row || !(row.patient_name || row.ip_number)) return null;
      return { patient_name: row.patient_name || null, ip_number: row.ip_number || null };
    },
    [stateById],
  );

  return { statusById: stateById, statusOf, occupantOf, loading, error, reload };
}

/**
 * useDeptAccessBlocked — true when the viewer has department memberships but
 * NONE is active (owner rule: sees no admissions until an admin reassigns).
 * Server word first (`dept_access_blocked` on the list response, true OR false); otherwise
 * derived from the SAME department store `useScope` reads: edges non-empty and
 * none in the loaded ACTIVE list. No memberships at all = unassigned = org-wide
 * (false). Unknown (edges/list not loaded, or list failed) = false. Fetches the
 * department list only for a viewer with edges and only when missing.
 */
export function useDeptAccessBlocked(serverFlag) {
  const { isAdmin, viewerDeptIds } = useScope();
  const departments = useDepartmentStore((s) => s.departments);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const deptsLoading = useDepartmentStore((s) => s.isLoading);
  const deptsError = useDepartmentStore((s) => s.error);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const hasEdges = !isAdmin && Array.isArray(viewerDeptIds) && viewerDeptIds.length > 0;
  useEffect(() => {
    if (hasEdges && !deptsLoaded && !deptsLoading) fetchDepartments();
  }, [hasEdges, deptsLoaded, deptsLoading, fetchDepartments]);
  // The server's word wins in BOTH directions (false = served org-wide: a
  // non-walled role, or an org with no active department). Derive only when absent.
  if (typeof serverFlag === 'boolean') return serverFlag;
  if (!hasEdges || !deptsLoaded || deptsError) return false;
  const active = new Set((departments || []).filter((d) => d.active !== false).map((d) => d.dept_id || d.id));
  return !viewerDeptIds.some((id) => active.has(id));
}
